import React, { useState, useEffect, useCallback, useRef } from 'react';
import { pmsAPI, branchPageAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import PmsProviderFields from '../../components/PmsProviderFields/PmsProviderFields';
import DataTable from '../../components/DataTable/DataTable';
import '../Dashboard/Dashboard.css';
import './Integrations.css';
import { formatDateTime, formatTime } from '../../utils/dateFormat';

// Display metadata for whichever provider a branch has selected — used by
// the table row and BranchDetailBody. Keep in sync with PmsProviderFields'
// own provider list.
const PROVIDER_META = {
    capitaland: { label: 'CapitaLand ODX', icon: 'fa-wifi' },
    wincloud:   { label: 'WinCloud PMS',   icon: 'fa-cloud' },
    winhms:     { label: 'WinHMS',         icon: 'fa-hotel' },
    idsnext:    { label: 'IDS Next',       icon: 'fa-building' },
    hotla:      { label: 'HOTLA',          icon: 'fa-wifi' },
    hotelkey:   { label: 'HotelKey',       icon: 'fa-key' },
    innkey:     { label: 'InnKey',         icon: 'fa-door-open' },
    fias:       { label: 'FIAS',           icon: 'fa-satellite-dish' },
};

const EMPTY_FORM = {
    integrations: {
        pms: { enabled: false, provider: 'capitaland', apiUrl: '', apiKey: '' },
        capitaland: { clientId: '', clientSecret: '', terminalId: '', propertyCode: '', environment: 'production', roomAuthSuffix: 'ORNRP', maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false, routesTo: 'rooms' },
        wincloud:   { serverUrl: '', propNm: '', cid: '', apiKey: '', syncInterval: 5, routesTo: 'rooms', maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
        winhms:     { serverUrl: '', hotelCode: '', username: '', password: '', syncInterval: 5, routesTo: 'rooms', maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
        idsnext:    { serverUrl: '', hotelCode: '', username: '', password: '', syncInterval: 5, routesTo: 'rooms', maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
        hotla:      { serverUrl: 'https://hotla.com/hotlaapi/wifiapi.php', propNm: '', apiKey: '', routesTo: 'rooms', syncInterval: 5, maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
        hotelkey:   { serverUrl: '', appId: '', appSecret: '', propertyId: '', propertyCode: '', inquiryUser: '', inquiryPassword: '', routesTo: 'rooms', syncInterval: 1, maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
        innkey:     { serverUrl: '', partner: '', accessKey: '', sharedKey: '', routesTo: 'rooms', syncInterval: 5, maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
        fias:       { propertyCode: '', guestSync: true, roomSync: true, routesTo: 'rooms', maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false },
    },
};

// Never sent back to the server: `<key>Set` flags (the key itself is write-only).
const withoutSetFlags = (cfg) => Object.fromEntries(Object.entries(cfg).filter(([k]) => !/Set$/.test(k)));

// Per-provider payload builder — each dedicated backend endpoint expects a
// flat body (not the nested formData shape), matching its own req.body
// destructure in pms.controller.js exactly.
const buildPayload = (formData) => {
    const { pms, capitaland, wincloud, winhms, idsnext, hotla, hotelkey, innkey, fias } = formData.integrations;
    const pmsEnabled = pms.enabled;
    switch (pms.provider) {
        case 'capitaland':
            return {
                pmsEnabled,
                clientId: capitaland.clientId, clientSecret: capitaland.clientSecret,
                terminalId: capitaland.terminalId, propertyCode: capitaland.propertyCode,
                environment: capitaland.environment, roomAuthSuffix: capitaland.roomAuthSuffix || '',
                maxDevicesPerRoom: capitaland.maxDevicesPerRoom ? Number(capitaland.maxDevicesPerRoom) : 0,
                ntfyTopic: capitaland.ntfyTopic || '', manualApproval: !!capitaland.manualApproval,
                routesTo: capitaland.routesTo || 'rooms',
            };
        case 'wincloud':
            return { pmsEnabled, ...wincloud };
        case 'winhms':
            return { pmsEnabled, ...winhms };
        case 'idsnext':
            return { pmsEnabled, ...idsnext };
        case 'hotla':
            return { pmsEnabled, ...hotla };
        // A blank key field keeps the saved key (the server never sends it back).
        case 'hotelkey':
            return { pmsEnabled, ...withoutSetFlags(hotelkey) };
        case 'innkey':
            return { pmsEnabled, ...withoutSetFlags(innkey) };
        case 'fias':
            // Webhook secret is not part of the form — it is generated in the
            // webhook panel (pmsAPI.generateWebhookSecret).
            return {
                pmsEnabled,
                propertyCode: fias.propertyCode, guestSync: fias.guestSync !== false, roomSync: fias.roomSync !== false,
                routesTo: fias.routesTo || 'rooms', maxDevicesPerRoom: fias.maxDevicesPerRoom ? Number(fias.maxDevicesPerRoom) : 0,
                ntfyTopic: fias.ntfyTopic || '', manualApproval: !!fias.manualApproval,
            };
        default:
            return { pmsEnabled };
    }
};

const saveByProvider = (provider, branchId, payload) => {
    switch (provider) {
        case 'capitaland': return pmsAPI.updateConfig(branchId, payload);
        case 'wincloud':   return pmsAPI.updateWincloudConfig(branchId, payload);
        case 'winhms':     return pmsAPI.updateWinhmsConfig(branchId, payload);
        case 'idsnext':    return pmsAPI.updateIdsnextConfig(branchId, payload);
        case 'hotla':      return pmsAPI.updateHotlaConfig(branchId, payload);
        case 'hotelkey':   return pmsAPI.updateHotelkeyConfig(branchId, payload);
        case 'innkey':     return pmsAPI.updateInnkeyConfig(branchId, payload);
        case 'fias':       return pmsAPI.updateFiasConfig(branchId, payload);
        default: throw new Error('Unknown PMS provider');
    }
};

// Single-room live lookup — every provider except CapitaLand has one of
// these (CapitaLand's equivalent is the "Room + Last Name" verify flow,
// which needs a last name too, not just a room number, so it's out of
// scope for this quick connectivity check). Lets an admin confirm the PMS
// is actually returning real guest data for one specific room right now,
// without waiting for the next periodic sync (see each provider's "Sync
// Now" button, and hotla.service.js's syncGuests for HOTLA specifically).
const roomLookupAPI = (provider) => ({
    wincloud: pmsAPI.getWincloudRoomGuest,
    winhms:   pmsAPI.getWinhmsRoomGuest,
    idsnext:  pmsAPI.getIdsnextRoomGuest,
    hotla:    pmsAPI.getHotlaRoomGuest,
    hotelkey: pmsAPI.getHotelkeyRoomGuest,
    innkey:   pmsAPI.getInnkeyRoomGuest,
}[provider] || null);

// Best-effort normalization across providers' differing raw field names —
// HOTLA's verifyRoomStatus() already returns a clean shape; WinCloud/WinHMS/
// IDS Next return whatever their own API sends, so this checks the common
// variants seen across those integrations without assuming any one is
// authoritative. Falls back to the raw JSON view when nothing matches.
const summarizeGuestResult = (data) => {
    if (!data) return null;
    const pick = (...keys) => keys.map(k => data[k]).find(v => v !== undefined && v !== null && v !== '');
    return {
        name:      pick('guestName', 'GuestName', 'name') || [pick('guestFirstName', 'GuestFirstName'), pick('guestLastName', 'GuestLastName')].filter(Boolean).join(' ') || null,
        mobile:    pick('mobile', 'Mobile', 'MobileNo'),
        email:     pick('email', 'Email'),
        checkIn:   pick('checkInDate', 'CheckinDate', 'CheckInDate'),
        checkOut:  pick('checkOutDate', 'CheckoutDate', 'CheckOutDate'),
        allowed:   data.allowed,
        message:   pick('message', 'Message'),
    };
};

const Integrations = () => {
    const admin = useAuthStore((state) => state.admin);
    const [branches, setBranches]       = useState([]);
    const [loading, setLoading]         = useState(true);
    const [saving, setSaving]           = useState(false);
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    const [showModal, setShowModal]     = useState(false);
    const [activeBranch, setActiveBranch] = useState(null);
    const [viewingBranch, setViewingBranch] = useState(null);
    const [formData, setFormData]       = useState(EMPTY_FORM);
    const [modalBranchPages, setModalBranchPages] = useState([]);
    // Live PMS reachability — keyed by branchId. Auto-checked on load and
    // every 60 seconds for configured branches. Provider-agnostic: every
    // provider's checkStatus() returns the same {up, latencyMs, message} shape.
    const [liveStatuses, setLiveStatuses] = useState({});
    const [checkingIds, setCheckingIds]   = useState({});
    const [lastChecked, setLastChecked]   = useState(null);
    const autoRefreshRef = useRef(null);

    useEffect(() => { fetchConfigs(); }, []);

    const checkLiveStatus = useCallback(async (branchId) => {
        setCheckingIds(prev => ({ ...prev, [branchId]: true }));
        try {
            const res = await pmsAPI.checkStatus(branchId);
            setLiveStatuses(prev => ({ ...prev, [branchId]: { ...res.data.data, checkedAt: new Date() } }));
        } catch (err) {
            setLiveStatuses(prev => ({
                ...prev,
                [branchId]: { up: false, message: err.response?.data?.message || 'Status check failed', checkedAt: new Date() },
            }));
        } finally {
            setCheckingIds(prev => ({ ...prev, [branchId]: false }));
        }
    }, []);

    const checkAllStatuses = useCallback(async (branchList) => {
        const configured = (branchList || branches).filter(b => b.configured && b.pms?.enabled);
        if (!configured.length) return;
        setLastChecked(new Date());
        await Promise.all(configured.map(b => checkLiveStatus(b.branchId)));
    }, [branches, checkLiveStatus]);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            const params = admin?.role === 'superadmin' && admin?.tenantId
                ? { tenantId: admin.tenantId }
                : {};
            const res = await pmsAPI.getConfigs(params);
            const data = res.data.data || [];
            setBranches(data);
            // Auto-check all configured branches immediately after loading
            const configured = data.filter(b => b.configured && b.pms?.enabled);
            if (configured.length) {
                setLastChecked(new Date());
                configured.forEach(b => {
                    setCheckingIds(prev => ({ ...prev, [b.branchId]: true }));
                    pmsAPI.checkStatus(b.branchId)
                        .then(r => setLiveStatuses(prev => ({ ...prev, [b.branchId]: { ...r.data.data, checkedAt: new Date() } })))
                        .catch(() => setLiveStatuses(prev => ({ ...prev, [b.branchId]: { up: false, message: 'Unreachable', checkedAt: new Date() } })))
                        .finally(() => setCheckingIds(prev => ({ ...prev, [b.branchId]: false })));
                });
            }
        } catch {
            toast.error('Failed to load PMS configurations');
        } finally {
            setLoading(false);
        }
    };

    // Auto-refresh every 60 seconds
    useEffect(() => {
        if (!branches.length) return;
        if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = setInterval(() => checkAllStatuses(branches), 60000);
        return () => clearInterval(autoRefreshRef.current);
    }, [branches, checkAllStatuses]);

    const loadSystemDefaults = async () => {
        if (loadingDefaults) return;
        setLoadingDefaults(true);
        try {
            const res = await pmsAPI.getSystemDefaults();
            if (!res.data.configured) {
                toast.warn('No CapitaLand credentials configured in server environment');
                return;
            }
            const d = res.data.data;
            setFormData(f => ({
                ...f,
                integrations: {
                    ...f.integrations,
                    pms: { ...f.integrations.pms, provider: 'capitaland', enabled: true },
                    capitaland: {
                        ...f.integrations.capitaland,
                        clientId:     d.clientId     || '',
                        clientSecret: d.clientSecret || '',
                        terminalId:   d.terminalId   || '',
                        propertyCode: d.propertyCode || '',
                        environment:  d.environment  || 'production',
                    },
                },
            }));
            toast.success('System credentials loaded — review and save');
        } catch {
            toast.error('Failed to load system credentials');
        } finally {
            setLoadingDefaults(false);
        }
    };

    // Generic dot-path updater — same pattern as Branches.jsx's own
    // handleInputChange, needed here so PmsProviderFields (shared between
    // both pages) can bind to either page's state identically.
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const parts = name.split('.');
        setFormData(prev => {
            const updated = { ...prev };
            let current = updated;
            for (let i = 0; i < parts.length - 1; i++) {
                current[parts[i]] = { ...current[parts[i]] };
                current = current[parts[i]];
            }
            current[parts[parts.length - 1]] = type === 'checkbox' ? checked : value;
            return updated;
        });
    };

    const openEdit = async (branch) => {
        setActiveBranch(branch);
        setFormData({
            integrations: {
                pms: {
                    enabled:  branch.pms?.enabled  || false,
                    provider: branch.pms?.provider || 'capitaland',
                    apiUrl: '', apiKey: '',
                },
                capitaland: { ...EMPTY_FORM.integrations.capitaland, ...(branch.capitaland || {}), clientId: '', clientSecret: '' },
                wincloud:   { ...EMPTY_FORM.integrations.wincloud,   ...(branch.wincloud   || {}), apiKey: '' },
                winhms:     { ...EMPTY_FORM.integrations.winhms,     ...(branch.winhms     || {}), password: '' },
                idsnext:    { ...EMPTY_FORM.integrations.idsnext,    ...(branch.idsnext    || {}), password: '' },
                hotla:      { ...EMPTY_FORM.integrations.hotla,      ...(branch.hotla      || {}), apiKey: '' },
                // Keys arrive blank with appSecretSet / inquiryPasswordSet / accessKeySet / sharedKeySet.
                hotelkey:   { ...EMPTY_FORM.integrations.hotelkey,   ...(branch.hotelkey   || {}), appSecret: '', inquiryPassword: '' },
                innkey:     { ...EMPTY_FORM.integrations.innkey,     ...(branch.innkey     || {}), accessKey: '', sharedKey: '' },
                fias:       { ...EMPTY_FORM.integrations.fias,       ...(branch.fias       || {}) },
            },
        });
        setShowModal(true);
        try {
            const res = await branchPageAPI.getAll(branch.branchId);
            setModalBranchPages(res.data.data || []);
        } catch {
            setModalBranchPages([]);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!activeBranch) return;

        const provider = formData.integrations.pms.provider;
        const p = formData.integrations[provider];

        // Required-field checks mirror each provider's own configured-check
        // in getConfigs/checkPmsStatus, so a "saved" branch is always one
        // that will actually show as configured and be reachable-checked.
        const requiredMissing = {
            capitaland: !p.terminalId || !p.propertyCode,
            wincloud:   !p.serverUrl || !p.propNm,
            winhms:     !p.serverUrl || !p.username,
            idsnext:    !p.serverUrl || !p.username,
            hotla:      !p.propNm,
            hotelkey:   !p.serverUrl?.trim() || !p.appId?.trim() || (!p.appSecret && !p.appSecretSet),
            innkey:     !p.serverUrl?.trim() || !p.partner?.trim() || (!p.accessKey && !p.accessKeySet) || (!p.sharedKey && !p.sharedKeySet),
            fias:       !p.propertyCode?.trim(),
        }[provider];
        if (requiredMissing) {
            toast.error('Please fill in the required fields for this provider');
            return;
        }

        try {
            setSaving(true);
            const payload = buildPayload(formData);
            await saveByProvider(provider, activeBranch.branchId, payload);
            toast.success(`PMS config saved for ${activeBranch.branchName}`);
            setShowModal(false);
            fetchConfigs();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const stats = {
        total:      branches.length,
        configured: branches.filter(b => b.configured).length,
        active:     branches.filter(b => b.pms?.enabled && b.configured).length,
    };

    return (
        <div className="page-container-modern">
            {/* Header */}
            <div className="page-header-modern">
                <div>
                    <h1 className="page-title-modern">
                        <i className="fas fa-plug" style={{ marginRight: 10, color: '#6366f1' }}></i>
                        PMS Integrations
                    </h1>
                    <p className="page-subtitle-modern">
                        Manage property-management-system WiFi verification per branch — CapitaLand ODX, WinCloud, WinHMS, IDS Next, HOTLA, HotelKey or InnKey
                        {lastChecked && (
                            <span style={{ marginLeft: 12, fontSize: '0.78rem', color: '#6b7280' }}>
                                <i className="fas fa-circle" style={{ color: '#22c55e', fontSize: 7, marginRight: 4 }}></i>
                                Auto-checked · Last: {formatTime(lastChecked)}
                            </span>
                        )}
                    </p>
                </div>
                <button
                    className="btn-primary-modern"
                    style={{ height: 38, whiteSpace: 'nowrap' }}
                    onClick={() => checkAllStatuses()}
                    disabled={Object.values(checkingIds).some(Boolean)}
                >
                    <i className={`fas ${Object.values(checkingIds).some(Boolean) ? 'fa-spinner fa-spin' : 'fa-rotate'}`}></i>
                    {Object.values(checkingIds).some(Boolean) ? ' Checking…' : ' Refresh All'}
                </button>
            </div>

            {/* Stats */}
            <div className="integ-stats-row">
                <div className="integ-stat-card">
                    <div className="integ-stat-icon" style={{ background: '#ede9fe' }}>
                        <i className="fas fa-code-branch" style={{ color: '#7c3aed' }}></i>
                    </div>
                    <div>
                        <p className="integ-stat-label">Total Branches</p>
                        <p className="integ-stat-value">{stats.total}</p>
                    </div>
                </div>
                <div className="integ-stat-card">
                    <div className="integ-stat-icon" style={{ background: '#e3e8f0' }}>
                        <i className="fas fa-key" style={{ color: '#1f3655' }}></i>
                    </div>
                    <div>
                        <p className="integ-stat-label">Credentials Set</p>
                        <p className="integ-stat-value">{stats.configured}</p>
                    </div>
                </div>
                <div className="integ-stat-card">
                    <div className="integ-stat-icon" style={{ background: '#dcfce7' }}>
                        <i className="fas fa-wifi" style={{ color: '#16a34a' }}></i>
                    </div>
                    <div>
                        <p className="integ-stat-label">Active PMS</p>
                        <p className="integ-stat-value">{stats.active}</p>
                    </div>
                </div>
                <div className="integ-stat-card">
                    <div className="integ-stat-icon" style={{ background: '#fef9c3' }}>
                        <i className="fas fa-exclamation-triangle" style={{ color: '#ca8a04' }}></i>
                    </div>
                    <div>
                        <p className="integ-stat-label">Not Configured</p>
                        <p className="integ-stat-value">{stats.total - stats.configured}</p>
                    </div>
                </div>
            </div>

            {/* Branch Table */}
            {loading ? (
                <div className="loading-state-modern">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading integrations...</p>
                </div>
            ) : (
                <div className="ad-card">
                    <div className="ad-card-header">
                        <div className="ad-card-title">
                            <i className="fas fa-plug"></i>
                            <h2>Branches</h2>
                        </div>
                    </div>
                    <div style={{ padding: '0 1.375rem 1.375rem' }}>
                        <DataTable
                            emptyMessage="No branches found"
                            columns={[
                                {
                                    label: 'Branch', field: 'branchName', filter: true,
                                    render: (row) => (
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{row.branch.branchName}</div>
                                            {row.branch.branchCode && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.branch.branchCode}</div>}
                                        </div>
                                    ),
                                    searchValue: (row) => `${row.branch.branchName} ${row.branch.branchCode || ''}`,
                                },
                                {
                                    label: 'Provider', field: 'providerLabel',
                                    render: (row) => row.meta ? (<span><i className={`fas ${row.meta.icon}`} style={{ marginRight: 6, color: '#6366f1' }}></i>{row.meta.label}</span>) : '—',
                                },
                                {
                                    label: 'PMS Status', field: 'pmsStatusText', sortable: false,
                                    render: (row) => <StatusBadge active={row.active} configured={row.branch.configured} />,
                                },
                                { label: 'Credentials', field: 'credentialsText' },
                                {
                                    label: 'API Status', field: 'apiStatusText', sortable: false,
                                    render: (row) => {
                                        if (row.checking) return <span><i className="fas fa-spinner fa-spin" style={{ marginRight: 4 }}></i> Checking…</span>;
                                        if (!row.liveStatus) return '—';
                                        return (
                                            <span
                                                className={`integ-env-badge ${row.liveStatus.up ? 'prod' : ''}`}
                                                style={!row.liveStatus.up ? { background: '#fee2e2', color: '#b91c1c' } : undefined}
                                                title={row.liveStatus.message}
                                            >
                                                <i className="fas fa-circle" style={{ fontSize: 7, marginRight: 5 }}></i>
                                                {row.liveStatus.up ? `Online${row.liveStatus.latencyMs ? ` (${row.liveStatus.latencyMs}ms)` : ''}` : 'Unreachable'}
                                            </span>
                                        );
                                    },
                                },
                                {
                                    label: 'Action', field: 'action', sortable: false, searchValue: () => '',
                                    render: (row) => (
                                        <div className="table-row-actions">
                                            <button className="action-btn-icon view" onClick={() => setViewingBranch(row.branch)} title="View"><i className="fas fa-eye"></i></button>
                                            {row.branch.configured && row.branch.pms?.enabled && (
                                                <button className="action-btn-icon" onClick={() => checkLiveStatus(row.branch.branchId)} title="Check status" disabled={row.checking}>
                                                    <i className={`fas ${row.checking ? 'fa-spinner fa-spin' : 'fa-satellite-dish'}`}></i>
                                                </button>
                                            )}
                                            <button className="action-btn-icon edit" onClick={() => openEdit(row.branch)} title={row.branch.configured ? 'Edit' : 'Configure'}>
                                                <i className={`fas fa-${row.branch.configured ? 'edit' : 'plus'}`}></i>
                                            </button>
                                        </div>
                                    ),
                                },
                            ]}
                            rows={branches.map((branch) => {
                                const meta = PROVIDER_META[branch.pms?.provider] || null;
                                const liveStatus = liveStatuses[branch.branchId];
                                const checking = !!checkingIds[branch.branchId];
                                return {
                                    id: branch.branchId,
                                    branch, meta, liveStatus, checking,
                                    branchName: branch.branchName,
                                    providerLabel: meta?.label || '—',
                                    active: branch.pms?.enabled && branch.configured,
                                    credentialsText: branch.configured ? 'Configured' : 'Not set',
                                    apiStatusText: checking ? 'Checking' : liveStatus ? (liveStatus.up ? 'Online' : 'Unreachable') : '—',
                                };
                            })}
                        />
                    </div>
                </div>
            )}

            {/* Config Modal */}
            {showModal && activeBranch && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>
                                    <i className="fas fa-plug" style={{ marginRight: 8, color: '#6366f1' }}></i>
                                    Configure PMS
                                </h2>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>
                                    {activeBranch.branchName}
                                    {activeBranch.branchCode && (
                                        <span className="integ-code-badge">{activeBranch.branchCode}</span>
                                    )}
                                </p>
                            </div>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSave}>
                            <div className="modal-body-modern">
                                <PmsProviderFields
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    modalBranchPages={modalBranchPages}
                                    admin={admin}
                                    loadingDefaults={loadingDefaults}
                                    onLoadDefaults={loadSystemDefaults}
                                    branchId={activeBranch.branchId}
                                />
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-modern" disabled={saving}>
                                    {saving ? (
                                        <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                                    ) : (
                                        <><i className="fas fa-save"></i> Save Configuration</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Branch Detail (View) Modal */}
            {viewingBranch && (
                <div className="modal-overlay-modern" onClick={() => setViewingBranch(null)}>
                    <div className="modal-modern" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{viewingBranch.branchName}</h2>
                                {viewingBranch.branchCode && (
                                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>
                                        <span className="integ-code-badge">{viewingBranch.branchCode}</span>
                                    </p>
                                )}
                            </div>
                            <button className="modal-close" onClick={() => setViewingBranch(null)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <BranchDetailBody
                                branch={viewingBranch}
                                liveStatus={liveStatuses[viewingBranch.branchId]}
                                checking={!!checkingIds[viewingBranch.branchId]}
                                onCheckStatus={() => checkLiveStatus(viewingBranch.branchId)}
                            />
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setViewingBranch(null)}>Close</button>
                            <button className="btn-primary-modern" onClick={() => { const b = viewingBranch; setViewingBranch(null); openEdit(b); }}>
                                <i className={`fas fa-${viewingBranch.configured ? 'edit' : 'plus'}`}></i>
                                {viewingBranch.configured ? 'Edit Configuration' : 'Configure PMS'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ── Branch Detail Body — provider summary, live API status, and the
     per-provider "Test Room Lookup" accordion. Shared by the View modal. ── */
const BranchDetailBody = ({ branch, liveStatus, checking, onCheckStatus }) => {
    const { configured } = branch;
    const active = branch.pms?.enabled && configured;
    const provider = branch.pms?.provider;
    const meta = PROVIDER_META[provider] || null;
    const isCapitaland = provider === 'capitaland';
    const lookupFn = roomLookupAPI(provider);
    const canTestLookup = isCapitaland || !!lookupFn;

    const [showLookup, setShowLookup] = useState(false);
    const [roomNo, setRoomNo]         = useState('');
    const [lastName, setLastName]     = useState(''); // CapitaLand's Room+LastName method only
    const [lookingUp, setLookingUp]   = useState(false);
    const [lookupResult, setLookupResult] = useState(null); // { ok, summary, raw, message }

    // CapitaLand's verify endpoint is a different shape than the other 4:
    // it needs Room Number + Last Name (not room number alone — ODX has no
    // "get room" lookup, only a real guest-verify call), appends the same
    // roomAuthSuffix real guest logins use so this matches what a guest
    // actually experiences, and its response is unwrapped {success,allowed,
    // checkOutDate,message} rather than {data:<raw vendor guest object>}
    // with a 403 on denial rather than a 200-with-no-guest.
    const handleCapitalandLookup = async (e) => {
        e.preventDefault();
        if (!roomNo.trim() || !lastName.trim()) return;
        setLookingUp(true);
        setLookupResult(null);
        try {
            const suffix = branch.capitaland?.roomAuthSuffix || 'ORNRP';
            const res = await pmsAPI.verifyWifi({
                branchId: branch.branchId,
                roomId: `${roomNo.trim()} ${suffix}`,
                primaryGuestLastName: lastName.trim(),
            });
            const data = res.data;
            setLookupResult({ ok: true, summary: { allowed: data.allowed, checkOut: data.checkOutDate, message: data.message }, raw: data });
        } catch (err) {
            const data = err.response?.data;
            if (data && typeof data.allowed === 'boolean') {
                // A real "denied" response (HTTP 403), not a request/config error
                setLookupResult({ ok: true, summary: { allowed: data.allowed, message: data.message }, raw: data });
            } else {
                setLookupResult({ ok: false, message: data?.message || 'Lookup failed' });
            }
        } finally {
            setLookingUp(false);
        }
    };

    const handleLookup = async (e) => {
        e.preventDefault();
        if (isCapitaland) return handleCapitalandLookup(e);
        if (!roomNo.trim() || !lookupFn) return;
        setLookingUp(true);
        setLookupResult(null);
        try {
            const res = await lookupFn(branch.branchId, roomNo.trim());
            const data = res.data?.data ?? null;
            if (!data) {
                setLookupResult({ ok: false, message: 'No guest found for this room number.' });
            } else {
                setLookupResult({ ok: true, summary: summarizeGuestResult(data), raw: data });
            }
        } catch (err) {
            setLookupResult({ ok: false, message: err.response?.data?.message || 'Lookup failed' });
        } finally {
            setLookingUp(false);
        }
    };

    return (
        <div className="integ-branch-detail">
            <div style={{ marginBottom: '0.75rem' }}>
                <StatusBadge active={active} configured={configured} />
            </div>

            {branch.location?.city && (
                <p className="integ-card-location">
                    <i className="fas fa-map-marker-alt"></i>
                    {[branch.location.city, branch.location.state].filter(Boolean).join(', ')}
                </p>
            )}

            <div className="integ-card-body">
                {configured && meta ? (
                    <>
                        <div className="integ-provider-chip">
                            <i className={`fas ${meta.icon}`}></i> {meta.label}
                        </div>
                        {Object.entries(branch.summaryFields || {}).map(([label, value]) => value ? (
                            <div className="integ-detail-row" key={label}>
                                <span className="integ-detail-label">{label}</span>
                                <span className="integ-detail-value integ-mono">{value}</span>
                            </div>
                        ) : null)}
                        <div className="integ-detail-row">
                            <span className="integ-detail-label">API Status</span>
                            {checking ? (
                                <span className="integ-detail-value">
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: 4 }}></i> Checking…
                                </span>
                            ) : liveStatus ? (
                                <div>
                                    <span
                                        className={`integ-env-badge ${liveStatus.up ? 'prod' : ''}`}
                                        style={!liveStatus.up ? { background: '#fee2e2', color: '#b91c1c' } : undefined}
                                        title={liveStatus.message}
                                    >
                                        <i className="fas fa-circle" style={{ fontSize: 7, marginRight: 5 }}></i>
                                        {liveStatus.up ? `Online${liveStatus.latencyMs ? ` (${liveStatus.latencyMs}ms)` : ''}` : 'Unreachable'}
                                    </span>
                                    {liveStatus.checkedAt && (
                                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>
                                            {formatTime(liveStatus.checkedAt)} · auto-refresh 60s
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onCheckStatus}
                                    style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer', color: '#374151' }}
                                >
                                    <i className="fas fa-satellite-dish"></i> Check now
                                </button>
                            )}
                        </div>

                        {canTestLookup && (
                            <div className="integ-room-lookup">
                                <button
                                    type="button"
                                    className="integ-lookup-toggle"
                                    onClick={() => setShowLookup(v => !v)}
                                >
                                    <i className={`fas fa-chevron-${showLookup ? 'down' : 'right'}`} style={{ fontSize: 10, marginRight: 6 }}></i>
                                    Test Room Lookup
                                </button>
                                {showLookup && (
                                    <div className="integ-lookup-body">
                                        <p className="integ-lookup-hint">
                                            {provider === 'hotla'
                                                ? 'Room Management also syncs from HOTLA periodically (see Sync Now below) — use this to check one room live without waiting for the next sync.'
                                                : isCapitaland
                                                    ? 'Runs the exact same Room Number + Last Name check a guest triggers at the captive portal (the auth suffix is added automatically).'
                                                    : `Look up a single room's live data straight from ${meta?.label || provider}.`}
                                        </p>
                                        <form onSubmit={handleLookup} className="integ-lookup-form">
                                            <input
                                                placeholder="Room number, e.g. 410"
                                                value={roomNo}
                                                onChange={e => setRoomNo(e.target.value)}
                                                autoComplete="off"
                                            />
                                            {isCapitaland && (
                                                <input
                                                    placeholder="Last name"
                                                    value={lastName}
                                                    onChange={e => setLastName(e.target.value)}
                                                    autoComplete="off"
                                                />
                                            )}
                                            <button type="submit" className="btn-secondary-modern" disabled={lookingUp || !roomNo.trim() || (isCapitaland && !lastName.trim())}>
                                                <i className={`fas ${lookingUp ? 'fa-spinner fa-spin' : 'fa-search'}`}></i>
                                            </button>
                                        </form>
                                        {lookupResult && (
                                            lookupResult.ok ? (
                                                <div className="integ-lookup-result">
                                                    {!lookupResult.summary?.name && lookupResult.summary?.message && (
                                                        <p className="integ-lookup-error" style={{ marginBottom: 6 }}>
                                                            <i className="fas fa-info-circle"></i> {lookupResult.summary.message}
                                                        </p>
                                                    )}
                                                    {lookupResult.summary?.name && <div className="integ-detail-row"><span className="integ-detail-label">Guest</span><span className="integ-detail-value">{lookupResult.summary.name}</span></div>}
                                                    {lookupResult.summary?.mobile && <div className="integ-detail-row"><span className="integ-detail-label">Mobile</span><span className="integ-detail-value integ-mono">{lookupResult.summary.mobile}</span></div>}
                                                    {lookupResult.summary?.checkOut && <div className="integ-detail-row"><span className="integ-detail-label">Checkout</span><span className="integ-detail-value">{formatDateTime(lookupResult.summary.checkOut)}</span></div>}
                                                    {typeof lookupResult.summary?.allowed === 'boolean' && (
                                                        <div className="integ-detail-row">
                                                            <span className="integ-detail-label">WiFi Allowed</span>
                                                            <span className={`integ-env-badge ${lookupResult.summary.allowed ? 'prod' : ''}`} style={!lookupResult.summary.allowed ? { background: '#fee2e2', color: '#b91c1c' } : undefined}>
                                                                {lookupResult.summary.allowed ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <details className="integ-lookup-raw">
                                                        <summary>Raw response</summary>
                                                        <pre>{JSON.stringify(lookupResult.raw, null, 2)}</pre>
                                                    </details>
                                                </div>
                                            ) : (
                                                <p className="integ-lookup-error">
                                                    <i className="fas fa-exclamation-circle"></i> {lookupResult.message}
                                                </p>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="integ-not-configured">
                        <i className="fas fa-exclamation-circle"></i>
                        <p>{meta ? `${meta.label} credentials not configured` : 'No PMS provider configured'}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const StatusBadge = ({ active, configured }) => {
    if (active)      return <span className="integ-status-badge active"><i className="fas fa-circle"></i> Active</span>;
    if (configured)  return <span className="integ-status-badge idle"><i className="fas fa-circle"></i> Idle</span>;
    return               <span className="integ-status-badge none"><i className="fas fa-circle"></i> Not Set</span>;
};

export default Integrations;
